import React from 'react';
import { motion } from 'framer-motion';
import { useLocation, Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Share2, Linkedin, Twitter, Mail } from 'lucide-react';
export const InsightDetail = () => {
  const location = useLocation();
  const {
    slug
  } = useParams();
  // Try to get post from state, or use a fallback if navigated directly
  const post = location.state?.post || {
    title: slug ? slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Strategic Insight',
    category: 'Advisory',
    date: 'July 2024',
    author: 'Managing Partner',
    img: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80',
    excerpt: 'Navigating the complexities of modern business requires a proactive approach and deep technical mastery.'
  };
  if (!post) {
    return <Navigate to="/insights" data-id="element-381" data-mp="1uyq5mg0e1qp7" />;
  }
  return <main className="pt-20 bg-brand-warm-white min-h-screen" data-id="element-382" data-mp="1uwdhfl41ek3p">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-end pb-20" data-id="element-383" data-mp="2hx79p5s45jq3">
        <div className="absolute inset-0 z-0" data-id="element-384" data-mp="2dwnbdyvt0a2d">
          <img src={post.img} alt={post.title} className="w-full h-full object-cover" data-id="element-385" data-mp="2rv3kfwh52cno" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/60 to-transparent" data-id="element-386" data-mp="2muz0txkxp1t8" />
        </div>

        <div className="max-w-4xl mx-auto px-6 relative z-10 w-full" data-id="element-387" data-mp="30u8eaphr9hti">
          <Link to="/insights" className="inline-flex items-center gap-2 text-white/70 hover:text-brand-gold transition-colors mb-8 text-sm font-bold tracking-widest uppercase" data-id="element-388" data-mp="2wt52qf6qq7y3">
            <ArrowLeft size={16} data-id="element-389" data-mp="35nt61zsr9fk9" /> Back to Insights
          </Link>

          <div className="flex items-center gap-4 mb-6" data-id="element-390" data-mp="3kj96cmjxi7p3">
            <span className="bg-brand-gold text-white px-4 py-1 text-[10px] font-bold tracking-widest uppercase rounded-full" data-id="element-391" data-mp="3khobswxm6or8">
              {post.category}
            </span>
            <div className="text-white/60 text-xs uppercase tracking-widest flex items-center gap-3" data-id="element-392" data-mp="3mpgv0tvc6izs">
              <span data-id="element-393" data-mp="3rnibr85ai683">{post.date}</span>
              <span className="w-1 h-1 rounded-full bg-brand-gold" data-id="element-394" data-mp="go2in0flvfx" />
              <span data-id="element-395" data-mp="fcycm32qloa">{post.author}</span>
            </div>
          </div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-6xl font-display text-white leading-tight mb-6" data-id="element-396" data-mp="58fpyooajl1u">
            {post.title}
          </motion.h1>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24" data-id="element-397" data-mp="o579mz7qw26f">
        <div className="max-w-4xl mx-auto px-6 flex flex-col lg:flex-row gap-16" data-id="element-398" data-mp="o45w8o39b3lj">
          {/* Social Share Sidebar */}
          <div className="lg:w-24 flex-shrink-0" data-id="element-399" data-mp="2pxlp7rmtgujo">
            <div className="sticky top-32 flex lg:flex-col gap-6 items-center" data-id="element-400" data-mp="2pyene47pmbq7">
              <span className="text-brand-charcoal/40 text-xs font-bold uppercase tracking-widest lg:-rotate-90 lg:mb-8 whitespace-nowrap" data-id="element-401" data-mp="2iechs3xvf5x9">
                Share
              </span>
              <button className="w-10 h-10 rounded-full border border-brand-navy/10 flex items-center justify-center text-brand-navy hover:bg-brand-navy hover:text-white transition-all" data-id="element-402" data-mp="28nrhmc2jttc8">
                <Linkedin size={18} data-id="element-403" data-mp="31copdl94w7qq" />
              </button>
              <button className="w-10 h-10 rounded-full border border-brand-navy/10 flex items-center justify-center text-brand-navy hover:bg-brand-navy hover:text-white transition-all" data-id="element-404" data-mp="31c6vgv9y25lz">
                <Twitter size={18} data-id="element-405" data-mp="2rd7jp3tvttur" />
              </button>
              <button className="w-10 h-10 rounded-full border border-brand-navy/10 flex items-center justify-center text-brand-navy hover:bg-brand-navy hover:text-white transition-all" data-id="element-406" data-mp="2mg7d8rt4ep34">
                <Mail size={18} data-id="element-407" data-mp="1b0shtsgk85a4" />
              </button>
            </div>
          </div>

          {/* Article Body */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="prose prose-lg prose-brand max-w-none" data-id="element-408" data-mp="2zugay21a1ka7">
            <p className="text-2xl text-brand-navy font-display leading-relaxed mb-12" data-id="element-409" data-mp="2uw4zjmvc31pv">
              {post.excerpt || 'In an increasingly interconnected global economy, strategic foresight and rigorous financial planning are no longer optional—they are imperative for sustainable growth and risk mitigation.'}
            </p>

            <h3 className="text-3xl font-display text-brand-navy mt-12 mb-6" data-id="element-410" data-mp="3dluvidau1tjd">
              The Current Landscape
            </h3>
            <p className="text-brand-charcoal/80 leading-relaxed mb-8" data-id="element-411" data-mp="1x6tlp16pjnfz">
              Organizations today face a unique confluence of challenges:
              shifting regulatory frameworks, rapid technological disruption,
              and volatile market dynamics. Navigating this environment requires
              a multidisciplinary approach that integrates tax optimization,
              capital structuring, and operational strategy.
            </p>
            <p className="text-brand-charcoal/80 leading-relaxed mb-8" data-id="element-412" data-mp="p8fs24ttj6av">
              Our recent analysis indicates that companies proactively adapting
              their cross-border strategies are significantly outperforming
              their peers in both capital efficiency and market share
              acquisition.
            </p>

            <div className="my-16 p-10 bg-brand-navy text-white rounded-2xl relative overflow-hidden" data-id="element-413" data-mp="ac6t74duauyz">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-gold/20 rounded-full blur-[40px] -mr-16 -mt-16" data-id="element-414" data-mp="5jn5helfujgg" />
              <p className="text-2xl font-display italic relative z-10" data-id="element-415" data-mp="3dyfchteejghw">
                "Success in modern markets is defined not just by the ability to
                identify opportunities, but by the structural agility to execute
                on them efficiently."
              </p>
            </div>

            <h3 className="text-3xl font-display text-brand-navy mt-12 mb-6" data-id="element-416" data-mp="1hv0rkg71gaxf">
              Strategic Imperatives
            </h3>
            <ul className="space-y-4 mb-8 text-brand-charcoal/80" data-id="element-417" data-mp="1rmnc5359nmcu">
              <li className="flex items-start gap-4" data-id="element-418" data-mp="1rozq757wlckm">
                <div className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-gold flex-shrink-0" data-id="element-419" data-mp="u07vu4oksuqv" />
                <span data-id="element-420" data-mp="yxg51frqxofz">
                  <strong data-id="element-421" data-mp="yyriy8ewockt">Comprehensive Risk Assessment:</strong> Evaluating
                  exposure across multiple jurisdictions and regulatory regimes.
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-422" data-mp="18x71vo3s4fj8">
                <div className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-gold flex-shrink-0" data-id="element-423" data-mp="2fcrel76pxg29" />
                <span data-id="element-424" data-mp="2kbkluhvewatz">
                  <strong data-id="element-425" data-mp="2kaszlxnpfpgd">Capital Structure Optimization:</strong> Aligning debt
                  and equity profiles with long-term strategic objectives.
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-426" data-mp="3omcp3n36shn">
                <div className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-gold flex-shrink-0" data-id="element-427" data-mp="3r8r9n8ea89d" />
                <span data-id="element-428" data-mp="3v1yziechr8hf">
                  <strong data-id="element-429" data-mp="3q4qgkbjir0f4">Operational Agility:</strong> Building flexible
                  frameworks that can adapt to sudden market shifts.
                </span>
              </li>
            </ul>

            <p className="text-brand-charcoal/80 leading-relaxed mb-8" data-id="element-430" data-mp="3h424vnhllc5e">
              As we look to the future, the integration of these elements will
              separate market leaders from the rest. Consilium Advisory remains
              committed to providing the insights and execution capabilities
              necessary to navigate this complex terrain.
            </p>
          </motion.div>
        </div>
      </section>
    </main>;
};